/**
 * Turning a model's answer about a receipt into lines we'd put in a fridge.
 *
 * Built on `parseRecognition`, which already survives the code fences, prose
 * preambles and case drift that come back from a vision model. What's added
 * here is everything a receipt has that a shelf photo doesn't: a date, a
 * quantity with a unit, and a great deal of text that isn't shopping.
 */

import { UNITS } from "../food/types";
import type { Unit } from "../food/types";
import { isValidIsoDate } from "../../lib/date/isoDate";
import { parseLoosely, toRecognition } from "../vision/parseRecognition";
import type { ReceiptLine, ReceiptRead } from "./types";

/** Beyond this a "receipt" is something else, and we'd rather stop than scroll. */
const MAX_LINES = 80;
const MAX_QUANTITY = 100_000;

/**
 * Words a model returns that aren't one of our six units.
 *
 * A receipt says "kg", "g", "ea", "each", "x2", "ct". Anything unmapped becomes
 * "pcs", which is the honest default: we know it was bought, we just don't know
 * what to call one of it.
 */
const UNIT_SYNONYMS: Record<string, Unit> = {
  ea: "pcs", each: "pcs", ct: "pcs", count: "pcs", item: "pcs", items: "pcs",
  unit: "pcs", units: "pcs", x: "pcs", pc: "pcs", piece: "pcs", pieces: "pcs",
  gram: "g", grams: "g", gr: "g", gm: "g", grm: "g",
  kilo: "kg", kilos: "kg", kilogram: "kg", kilograms: "kg",
  millilitre: "ml", milliliter: "ml", millilitres: "ml", milliliters: "ml",
  litre: "l", liter: "l", litres: "l", liters: "l", lt: "l", ltr: "l", ltrs: "l",
  packet: "pack", packets: "pack", pkt: "pack", pk: "pack", packs: "pack",
  bag: "pack", box: "pack", punnet: "pack", tin: "pack", can: "pack",
  bottle: "pack", jar: "pack", bunch: "pack", tray: "pack",
};

export function toUnit(raw: unknown): Unit {
  if (typeof raw !== "string") return "pcs";
  const key = raw.trim().toLowerCase().replace(/[.\s]+/g, "");
  if ((UNITS as readonly string[]).includes(key)) return key as Unit;
  return UNIT_SYNONYMS[key] ?? "pcs";
}

/**
 * A quantity that survives the ways a receipt writes one.
 *
 * Returns 1 rather than null when it can't tell. A line that made it this far
 * is something that was bought; refusing it over an unreadable quantity would
 * lose a real item to protect a number the user can edit in one tap.
 */
function toQuantity(raw: unknown): number {
  const value =
    typeof raw === "number"
      ? raw
      : typeof raw === "string"
        ? Number(raw.replace(/[^\d.]/g, ""))
        : NaN;
  if (!Number.isFinite(value) || value <= 0) return 1;
  return Math.min(MAX_QUANTITY, value);
}

/**
 * The receipt's own date, if it gave a usable one.
 *
 * Refused rather than guessed when it isn't a real calendar date or is in the
 * future — a misread `03/09` becoming a purchase date in 2027 would push every
 * best-before on the shop out with it, and a wrong date that looks deliberate
 * is worse than no date at all. The sheet falls back to today and says so.
 */
export function toPurchaseDate(raw: unknown, today: string): string | null {
  if (typeof raw !== "string") return null;
  const value = raw.trim();
  if (!isValidIsoDate(value)) return null;
  // Fixed-width ISO dates compare as strings.
  if (value > today) return null;
  return value;
}

export function toReceiptLine(payload: unknown): ReceiptLine | null {
  // Name, category and shelf life are the same problem the shelf scanner
  // already solved, so they are solved in one place.
  const base = toRecognition(payload);
  if (!base) return null;

  const record = payload as Record<string, unknown>;
  return {
    ...base,
    quantity: toQuantity(record.quantity ?? record.qty ?? record.amount),
    unit: toUnit(record.unit ?? record.units ?? record.uom),
  };
}

/**
 * Everything usable in a receipt reply.
 *
 * Duplicates are *not* merged the way a shelf scan merges them. Two lines of
 * milk on a receipt means two milks were bought, and a shopper who sees one is
 * being told something false about their own kitchen — whereas two cartons in
 * one photo really is likely to be one carton seen twice.
 */
export function parseReceipt(text: string, today: string): ReceiptRead {
  const payload = parseLoosely(text);
  const record = (payload ?? {}) as Record<string, unknown>;

  const rawLines = Array.isArray(payload)
    ? payload
    : Array.isArray(record.items)
      ? record.items
      : Array.isArray(record.lines)
        ? record.lines
        : [];

  const lines: ReceiptLine[] = [];
  for (const entry of rawLines) {
    if (lines.length >= MAX_LINES) break;
    const line = toReceiptLine(entry);
    if (line) lines.push(line);
  }

  return {
    purchaseDate: toPurchaseDate(
      record.purchase_date ?? record.purchaseDate ?? record.date,
      today
    ),
    lines,
  };
}

/**
 * Several photos of one long receipt, as a single read.
 *
 * A 60cm receipt is not one picture, and pretending otherwise means quietly
 * losing the bottom third of a shop. Here the *photos* are the thing being
 * merged, so a name appearing in two of them is an overlap at the seam rather
 * than two purchases — that is the opposite of the rule inside one photo, and
 * it is the only place the two rules disagree.
 *
 * The first readable date wins: it's the same receipt.
 */
export function mergeReceipts(reads: ReceiptRead[]): ReceiptRead {
  const seen = new Set<string>();
  const lines: ReceiptLine[] = [];

  for (const read of reads) {
    for (const line of read.lines) {
      const key = `${line.name.toLowerCase()}|${line.quantity}|${line.unit}`;
      if (seen.has(key)) continue;
      seen.add(key);
      lines.push(line);
    }
  }

  return {
    purchaseDate: reads.find((read) => read.purchaseDate)?.purchaseDate ?? null,
    lines,
  };
}
